import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { computeBadges, fromDashboard } from "./badges";
import { VIEWBOX } from "./norway";
import { cardTitle, drawCard } from "./shareCard";

// Same values the card itself draws, joined into one sentence for a screen
// reader. A plain function so the draw effect below can keep computing its
// own counts/title independently, rather than threading them through state.
function cardAriaLabel(stats) {
	const counts = fromDashboard(stats);
	const title = cardTitle(computeBadges(counts));
	return [
		stats.displayName,
		title?.name,
		`${stats.totalPlayed} av ${stats.totalCourses} klubber`,
		`${counts.fylkerComplete} av ${counts.fylkerTotal} fylker fullført`,
	]
		.filter(Boolean)
		.join(", ");
}

// ponytail: fetches its own stats, like BadgesDialog. Everything on the card —
// displayName, totalCourses, regionStats — is in that one response, so the
// dialog needs no props beyond open/onClose.
function ShareMapDialog({ open, onClose }) {
	const [stats, setStats] = useState(null);
	const [shareError, setShareError] = useState(false);
	const canvasRef = useRef(null);

	useEffect(() => {
		if (!open) return;

		setStats(null);
		setShareError(false);
		let cancelled = false;
		fetch("/api/overview")
			.then((res) => (res.ok ? res.json() : Promise.reject()))
			.then((body) => {
				if (!cancelled) setStats(body);
			})
			.catch(() => {
				if (!cancelled) setStats("error");
			});

		return () => {
			cancelled = true;
			// Reopening should start from the spinner, not a frame of last time's
			// stats painted onto a fresh canvas before this effect refetches.
			setStats(null);
		};
	}, [open]);

	const failed = stats === "error";
	const ready = stats && !failed;

	useEffect(() => {
		if (!ready) return;

		let cancelled = false;
		// Canvas text does not wait for webfonts the way DOM text does. Drawing
		// early bakes a system fallback into the exported PNG, invisibly on any
		// machine where the font is already cached.
		Promise.resolve(document.fonts?.ready).then(() => {
			const ctx = cancelled ? null : canvasRef.current?.getContext("2d");
			if (!ctx) return;

			const counts = fromDashboard(stats);
			drawCard(ctx, {
				regionStats: stats.regionStats,
				name: stats.displayName,
				title: cardTitle(computeBadges(counts)),
				played: stats.totalPlayed,
				total: stats.totalCourses,
				fylkerComplete: counts.fylkerComplete,
				fylkerTotal: counts.fylkerTotal,
			});
		});

		return () => {
			cancelled = true;
		};
	}, [ready, stats]);

	const ariaLabel = ready ? cardAriaLabel(stats) : undefined;

	async function handleShare() {
		setShareError(false);
		try {
			const canvas = canvasRef.current;
			if (!canvas) throw new Error("Ingen canvas å eksportere.");

			const blob = await new Promise((resolve) =>
				canvas.toBlob(resolve, "image/png"),
			);
			if (!blob) throw new Error("Eksport av kartet feilet.");

			const file = new File([blob], "golfjakten.png", { type: "image/png" });

			if (navigator.canShare?.({ files: [file] })) {
				try {
					await navigator.share({ files: [file] });
					return;
				} catch (err) {
					// The user closing the share sheet is not a failure, and silently
					// downloading instead would be the app overruling them.
					if (err?.name === "AbortError") return;
				}
			}

			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "golfjakten.png";
			// Revoking on the same tick as click(), or clicking a link that was
			// never attached to the document, are both documented causes of
			// aborted downloads in some browsers.
			document.body.appendChild(link);
			link.click();
			link.remove();
			setTimeout(() => URL.revokeObjectURL(url), 0);
		} catch {
			setShareError(true);
		}
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="xs"
			slotProps={{ paper: { sx: { borderRadius: 3 } } }}
		>
			<DialogTitle sx={{ fontWeight: 700, color: "#2E7D32", pb: 0.5 }}>
				Del kartet
			</DialogTitle>

			<DialogContent sx={{ pb: 2 }}>
				{!stats ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress color="success" size={28} />
					</Box>
				) : failed ? (
					<Typography variant="body2" color="text.secondary">
						Kunne ikke hente kartet ditt.
					</Typography>
				) : (
					<>
						<canvas
							ref={canvasRef}
							width={VIEWBOX.width}
							height={VIEWBOX.height}
							style={{ width: "100%", display: "block" }}
							role="img"
							aria-label={ariaLabel}
						/>
						{shareError && (
							<Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
								Kunne ikke dele kartet.
							</Typography>
						)}
					</>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button onClick={onClose} color="inherit">
					Lukk
				</Button>
				{ready && (
					<Button variant="contained" color="success" onClick={handleShare}>
						Del
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default ShareMapDialog;
